/**
 * 项目文件归档：按类别/阶段浏览，支持紧凑缩略图和电脑文件式详细列表。
 * 本轮修复文件卡片未接收预览回调导致点击报错的问题；现场照片在当前分组内放大、连续翻看。
 * 展示方式只影响前端排版，上传、不可删除归档、图纸分发及敏感类别权限沿用现有业务规则。
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { uploadChunked } from "../lib/upload";
import { authToken } from "../lib/api";
import { useApp } from "../context/AppContext";
import { getPhase, ENGINE_PHASES, LEGACY_TO_ENGINE_PHASES } from "../data/constants";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FolderOpen, Upload, Trash2, FileText, Film, Image, LayoutGrid, List, Loader2, ExternalLink, FileSignature, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { SendDrawingDialog } from "./SendDrawingDialog";
import { ProjectPdfPreview } from "./pdf/ProjectPdfPreview";
import { PlanShareDialog } from "./pdf/PlanShareDialog";
import ProjectFileImagePreview from "./ProjectFileImagePreview";

import { showContractWarnings } from "../lib/contractWarn";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const authQ = () => `auth=${encodeURIComponent(authToken())}`;

/** 文件元信息缺失时显示破折号，避免把历史文件的未知大小/时间误写成 0 或今天。 */
const fileSize = (size) => {
  if (size == null || !Number.isFinite(Number(size)) || Number(size) < 0) return "—";
  const bytes = Number(size);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
const fileDate = (value, zh) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(zh ? "zh-CN" : "en-US") : "—";
};

/** 同一个文件入口用于网格和列表，确保两种视图的预览、图纸分发和删除规则一致。 */
const FileTile = ({ f, zh, thumbErr, setThumbErr, openPreview, setSendFile, del, viewMode }) => {
  const fileUrl = `${BACKEND}/api/files/${encodeURIComponent(f.fileId)}/download?${authQ()}`;
  const thumbUrl = `${BACKEND}/api/files/${encodeURIComponent(f.fileId)}/thumb?${authQ()}`;
  const detail = viewMode === "list";
  const Icon = f.kind === "image" ? Image : f.kind === "video" ? Film : FileText;
  const type = f.kind === "pdf" ? "PDF" : f.kind === "image" ? (zh ? "图片" : "Image") : f.kind === "video" ? (zh ? "视频" : "Video") : (zh ? "文件" : "File");
  const previewable = (f.kind === "image" || f.kind === "pdf") && !thumbErr[f.fileId];
  // 图片按比例完整显示，但限制缩略图高度，避免长图撑开整个文件分组；点击仍打开原有大图预览。
  const isImg = f.kind === "image";
  const thumbnail = (
    <span className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-50 ${detail ? "h-10 w-10" : isImg ? "w-full min-h-20" : "h-20 w-full"}`}>
      {previewable ? <img src={f.kind === "image" ? fileUrl : thumbUrl} alt="" loading="lazy" draggable={false}
        data-testid={f.kind === "pdf" ? `file-thumb-${f.id}` : undefined}
        onError={() => setThumbErr((m) => ({ ...m, [f.fileId]: true }))} className={`${isImg ? (detail ? "h-full w-full object-cover" : "h-auto max-h-48 w-full object-contain") : "h-full w-full object-contain"}`} />
        : <Icon className={`${detail ? "h-6 w-6" : "h-10 w-10"} text-brand-500`} aria-hidden="true" />}
      {f.kind === "pdf" && <span data-testid={`file-badge-${f.id}`} className={`absolute left-1 top-1 rounded bg-red-600 px-1 text-[10px] font-semibold text-white ${detail ? "sr-only" : ""}`}>PDF{f.pageCount ? ` · ${f.pageCount}${zh ? " 页" : "p"}` : ""}</span>}
    </span>
  );
  const open = (
    <button type="button" data-testid={`file-open-${f.id}`} onClick={(event) => openPreview(f, event.currentTarget)} title={f.filename}
      className={`flex w-full min-w-0 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${detail ? "items-center gap-3 p-1 hover:bg-slate-50" : "flex-col gap-2 p-2 hover:bg-brand-50"}`}>
      {thumbnail}
      <span className={`min-w-0 text-xs font-medium text-slate-700 ${detail ? "break-all" : "line-clamp-2 min-h-[2.5rem] w-full break-all leading-5"}`}>{f.filename}</span>
    </button>
  );
  const actions = (
    <div className={`flex items-center gap-1 ${detail ? "justify-end" : "border-t border-slate-100 px-1.5 py-1"}`}>
      {f.category === "approved_plan" && <Button type="button" data-testid={`file-send-sub-${f.id}`} onClick={() => setSendFile(f)} title={zh ? "发给分包报价参考" : "Send to subs"} variant="ghost" size="sm" className="h-7 gap-1 px-1.5 text-[11px] text-brand-600"><Send className="h-3 w-3" aria-hidden="true" />{zh ? "发给分包" : "Send"}</Button>}
      {!f.immutable && <Button type="button" data-testid={`file-delete-${f.id}`} onClick={() => del(f)} variant="ghost" size="icon" title={zh ? "删除文件" : "Delete file"} aria-label={`${zh ? "删除" : "Delete"} ${f.filename}`} className="ml-auto h-7 w-7 shrink-0 text-slate-400 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" aria-hidden="true" /></Button>}
    </div>
  );
  if (detail) return (
    <tr data-testid={`file-item-${f.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
      <td className="px-2 py-1">{open}</td>
      <td className="px-2 py-2 text-xs text-slate-500">{type}</td>
      <td className="px-2 py-2 text-right text-xs tabular-nums text-slate-500">{fileSize(f.size)}</td>
      <td className="px-2 py-2 text-xs text-slate-500">{fileDate(f.createdAt, zh)}</td>
      <td className="px-2 py-1">{actions}</td>
    </tr>
  );
  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid={`file-item-${f.id}`}>{open}{(f.category === "approved_plan" || !f.immutable) && actions}</div>
  );
};

/** 缩略图随浮窗宽度自动排布；详细表只在自身容器内滚动，不撑宽页面或浮窗。 */
const FileCollection = ({ items, openPreview, viewMode, zh, ...props }) => {
  if (!items.length) return null;
  const entries = items.map((f) => <FileTile key={f.id} f={f} zh={zh} viewMode={viewMode} openPreview={(file, trigger) => openPreview(file, items, trigger)} {...props} />);
  return viewMode === "list" ? (
    <div className="max-w-full overflow-x-auto rounded-lg border border-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" tabIndex={0} role="region" aria-label={zh ? "文件详细列表" : "File details"}>
      <table className="w-full min-w-[620px] table-fixed text-left">
        <thead className="bg-slate-50 text-[11px] text-slate-500"><tr>
          <th scope="col" className="px-3 py-2 font-medium">{zh ? "名称" : "Name"}</th>
          <th scope="col" className="w-16 px-2 py-2 font-medium">{zh ? "类型" : "Type"}</th>
          <th scope="col" className="w-20 px-2 py-2 text-right font-medium">{zh ? "大小" : "Size"}</th>
          <th scope="col" className="w-28 px-2 py-2 font-medium">{zh ? "上传日期" : "Uploaded"}</th>
          <th scope="col" className="w-32 px-2 py-2 text-right font-medium">{zh ? "操作" : "Actions"}</th>
        </tr></thead>
        <tbody>{entries}</tbody>
      </table>
    </div>
  ) : <div className="grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] items-start gap-2">{entries}</div>;
};
const CATS = [
  { key: "signed_archive", zh: "签约归档 Signed Archive" },
  { key: "scope_of_works", zh: "工作内容 Scope of Works" },
  { key: "approved_plan", zh: "批准图纸 Approved Plan" },
  { key: "inspection_card", zh: "验收卡 Inspection Card" },
  { key: "material_order_list", zh: "材料订单 Material Order List" },
  { key: "owner_signature", zh: "业主签字确认 Owner Signature" },
  { key: "design_change", zh: "设计变更确认 Design Change" },
  { key: "job_site_photos", zh: "工地现场照片 Job Site Photos" },
  { key: "payment_vouchers", zh: "付款凭证 Payment Vouchers" },
  { key: "payment_receipts", zh: "收款凭证 Payment Receipts" },
];
/**
 * 需要保留原始字节的项目文件类别。
 *
 * 需求来源（2026-10-23 微信群反馈）：项目文件里的图纸不能压缩，压缩后局部标注和尺寸看不清，
 * 上传归档就失去审阅与施工依据的意义。
 *
 * uploadChunked 默认会先在前端缩图，服务端 finalize 还会再走一次全局媒体压缩。图纸必须同时
 * 关闭这两层压缩，否则只关前端仍会得到服务端压缩版；其他类别继续沿用现有轻量预览策略。
 */
const LOSSLESS_CATS = new Set(["approved_plan"]);
/**
 * 现场照片在 project_files 有两个落库分类（与后端 project_hub._SITE_PHOTO_CATEGORIES 同口径）：
 *   · job_site_photos —— 项目详情「现场快照」卡片 / 本页「工地现场照片」上传；
 *   · engine_evidence —— 施工任务「上传证据」时同步归档的现场照片 / 视频。
 * 客户反馈（2026-10-23 微信群）：下面现场快照里能看到的照片，本页「工地现场照片」也要能看到，
 * 否则会以为文件丢了。所以这一组按两类媒体的并集展示；证据类的记录只读（不可在本页删除），
 * 它的删除入口仍归施工任务证据本身，避免把 evidence 归档行删掉而任务里还挂着。
 */
const SITE_MEDIA_CATS = ["job_site_photos", "engine_evidence"];
// job_site_photos 是本组原生分类，历史记录可能缺 contentType，必须照旧显示；
// engine_evidence 只在确实是图片/视频时并入，证据 PDF 不进「工地现场照片」。
const isSiteMedia = (f) => (f.category === "job_site_photos"
  ? true
  : SITE_MEDIA_CATS.includes(f.category) && /^(image|video)\//.test(String(f.contentType || "")));
const matchesCat = (f, key) => (key === "job_site_photos" ? isSiteMedia(f) : f.category === key);
/** 施工任务证据落库时 kind 只区分 image / file，视频会被记成 file；这里按 contentType 复原，
 *  否则同一张现场视频在本页会显示成普通文件、点不开预览。 */
const mediaKind = (f) => {
  const ct = String(f.contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  return f.kind;
};
// 敏感类别（合同归档/工作内容/资金凭证）仅 admin / CEO 可见；其余角色界面与数据均不出现（前端过滤，2026-10-16）
const ADMIN_ONLY_CATS = ["signed_archive", "scope_of_works", "payment_vouchers", "payment_receipts"];

/**
 * 展示 projectId 的项目归档。
 * focusCategory 用于从现场快照入口打开全部归档后自动定位并高亮对应分类；
 * categoryFilter 用于需要只展示单一分类的调用方，默认展示全部归档。
 * 两种入口共用上传、分组、预览和敏感类别权限，避免维护两套文件浏览逻辑。
 */
export default function ProjectFiles({
  projectId,
  focusCategory = "",
  categoryFilter = null,
}) {
  const { lang, api, user } = useApp();
  const zh = lang === "zh";
  const canSeeRestricted = user?.role === "admin" || user?.role === "ceo";
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sendFile, setSendFile] = useState(null);
  const [shareFile, setShareFile] = useState(null);
  const [thumbErr, setThumbErr] = useState({});
  const [groupBy, setGroupBy] = useState("category"); // category | phase
  const [viewMode, setViewMode] = useState("grid"); // 展示密度独立于类别/阶段分组，切换时不改变文件归档。
  const [photosOnly, setPhotosOnly] = useState(false);
  // 现场照片按点击时所在类别、子文件夹或阶段建立队列，不混入其他组或 PDF。
  // 只保存 ID，随后再与 visibleFiles 交叉过滤，保证列表/网格/翻页使用相同权限口径。
  const [previewIds, setPreviewIds] = useState([]);
  const previewTrigger = useRef(null);
  // 拖拽上传状态（需求 SYS-003）：记录当前拖到哪个类别的投放区，用来高亮提示「松手即上传」
  const [dragCat, setDragCat] = useState(null);
  const dragDepth = useRef({});
  const refs = useRef({});
  const fileUrl = (fid) => `${BACKEND}/api/files/${encodeURIComponent(fid)}/download?${authQ()}`;

  // 快捷入口的类别范围与权限一起过滤，避免切到「按阶段」或连续预览时混入其他类别文件。
  const visibleFiles = useMemo(
    () => files.filter((f) => (canSeeRestricted || !ADMIN_ONLY_CATS.includes(f.category)) && (!categoryFilter || matchesCat(f, categoryFilter))),
    [files, canSeeRestricted, categoryFilter]
  );

  // 【2026-10-21】外部入口定位分类（focusCategory）：项目详情页「现场快照」卡右上角的
  // 「文件归档 · 现场记录 N」原来错接到分包商管理弹窗，改成打开本组件后，需要直接落到
  // 「工地现场照片（job_site_photos）」这一组 —— 现场快照上传的落库分类就是它（见 project_hub.hub_photo_add）。
  // 实现要点：
  //   · 只认 CATS 里存在的分类，避免拼错参数时把界面滚到空处；
  //   · 定位只在「按类别」视图有效，因为「按阶段」视图没有分类分组，此时先切回按类别；
  //   · 用 scrollIntoView 而不是锚点跳转，是因为本组件通常挂在 FloatWindow / Dialog 的滚动容器里，
  //     锚点会改变整个页面的 hash 与滚动位置；
  //   · 高亮 3 秒后自动回落，属于「帮你找到入口」的过渡提示，不是常驻状态色。
  //     取 3 秒而不是 2 秒：用户是从「现场快照」卡跳到这个浮窗的，视线需要重新定位，2 秒常常还没看到就没了。
  const [flashCat, setFlashCat] = useState("");
  // 定位必须限定在本组件容器内：项目弹窗（ProjectDialog）里也挂着同一个 ProjectFiles，
  // 若用全局 querySelector，可能滚到另一个看不见的实例上。
  const rootRef = useRef(null);
  // 「本次打开只自动定位一次」：files.length 变化会重新触发下面的 effect（首次数据到达需要它），
  // 但用户随后上传文件也会改变长度 —— 那时再强行滚回现场照片分类会打断他正在看的分类。
  const focusedOnceRef = useRef("");
  const focusCatValid = useMemo(() => (CATS.some((c) => c.key === focusCategory) ? focusCategory : ""), [focusCategory]);
  useEffect(() => {
    if (!focusCatValid) return;
    if (groupBy !== "category") { setGroupBy("category"); return; }
    if (focusedOnceRef.current === focusCatValid) return;
    // 等文件列表回来再滚动：分类分组首帧就有骨架，但带文件时高度会在数据到达后变化，
    // 抢在数据前滚，位置会被随后的重排冲掉（实测滚完又弹回原处）。
    const t = setTimeout(() => {
      const el = rootRef.current?.querySelector(`[data-testid="file-cat-${focusCatValid}"]`);
      if (!el) { setFlashCat(focusCatValid); return; }
      // 直接设「最近一层滚动容器」的 scrollTop，而不是用 scrollIntoView。
      // 原因：本组件嵌在 FloatWindow 里，外面还有项目详情抽屉，scrollIntoView 会同时滚动
      // 所有可滚动祖先 —— 实测会把外层抽屉一起带上去，或者动画被重渲染打断后停回原处。
      // 这里只动浮窗内部那层，位置稳定、也不干扰用户看项目的上下文。
      let sc = el.parentElement;
      while (sc && sc !== document.body) {
        const oy = getComputedStyle(sc).overflowY;
        if (/(auto|scroll)/.test(oy) && sc.scrollHeight > sc.clientHeight + 4) break;
        sc = sc.parentElement;
      }
      if (sc && sc !== document.body) {
        const elTop = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
        // 留 8px 呼吸位；滚不到顶时浏览器会自动夹到上限，末位分类就停在「能看到」的位置。
        sc.scrollTop = Math.max(0, elTop - 8);
      } else {
        el.scrollIntoView({ behavior: "auto", block: "nearest" });
      }
      setFlashCat(focusCatValid);
      focusedOnceRef.current = focusCatValid;
    }, 220);
    return () => clearTimeout(t);
  }, [focusCatValid, groupBy, files.length]);
  useEffect(() => {
    if (!flashCat) return;
    const t = setTimeout(() => setFlashCat(""), 3000);
    return () => clearTimeout(t);
  }, [flashCat]);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}/files`); setFiles(data || []); } catch { setFiles([]); }
  }, [api, projectId]);
  useEffect(() => { load(); }, [load]);

  // 队列顺序固定为打开时看到的顺序；两种展示方式都从 FileCollection 传入同一组数据。
  const previewQueue = useMemo(() => {
    if (!preview || (preview.kind !== "image" && preview.kind !== "video")) return [];
    const byId = new Map(visibleFiles.map((f) => [f.id, f]));
    return previewIds.map((id) => byId.get(id)).filter((f) => f && (f.kind === "image" || f.kind === "video"));
  }, [preview, previewIds, visibleFiles]);

  const queuePos = previewQueue.length ? previewQueue.findIndex((f) => f.id === preview?.id) : -1;

  // 显式传入回调，修复 FileTile 访问父组件局部函数导致 ReferenceError 的问题。
  const openPreview = useCallback((f, items, trigger) => {
    previewTrigger.current = trigger;
    setPreviewIds(items.map((item) => item.id));
    setPreview(f);
  }, []);
  const stepPreview = useCallback((delta) => {
    if (queuePos < 0 || previewQueue.length < 2) return;
    const nextPos = (queuePos + delta + previewQueue.length) % previewQueue.length;
    setPreview(previewQueue[nextPos]);
  }, [queuePos, previewQueue]);
  // 方向键只在当前弹窗内响应，避免抢走视频播放器、输入框或叠加分享弹窗的按键。
  const onPreviewKey = (event) => {
    if (queuePos < 0 || previewQueue.length < 2 || shareFile || sendFile || event.target.closest("input, textarea, select, video, [contenteditable=true]")) return;
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      stepPreview(event.key === "ArrowLeft" ? -1 : 1);
    }
  };

  // 按阶段聚合（2026-10-24 改为「引擎 8 阶段」统一口径）。
  // 老板需求：上传时选了哪个阶段（没选=项目当前阶段），就显示在哪个阶段下面，
  // 且分组名要与现场快照轮播角标 / 上传下拉对得上。此前两个问题：
  //   1. 分组键用旧 7 阶段 phaseIndex：现场快照视频缺 phaseIndex 全落「未标注」；
  //      且「拆除」阶段在旧表没有槽位（旧 0=开工前准备），照片会错归。
  //   2. 分组名用旧 7 阶段名（地基/框架…），与引擎名（基础/框架结构…）文字对不上。
  // 现在分组键改为引擎阶段编号 0-8：现场快照直接用落库的引擎 phase；其它文件把
  // phaseIndex 经 LEGACY_TO_ENGINE_PHASES 映射过去；组名/色点统一用 ENGINE_PHASES。
  // 负数编号（设计阶段等）不在引擎体系内，保持原 getPhase 展示。
  const phaseGroups = useMemo(() => {
    // 「只看照片/视频」：现场快照 2026-10-15 起同时收视频，这里跟着放开，否则传了视频反而被藏起来
    const list = photosOnly ? visibleFiles.filter((f) => f.kind === "image" || f.kind === "video") : visibleFiles;
    const map = new Map();
    const engineIdxOf = (f) => {
      if (Number.isInteger(f.phase) && f.phase >= 0 && f.phase <= 8) return f.phase;
      let idx = f.phaseIndex;
      if (typeof idx === "string" && idx.trim() !== "") idx = parseInt(idx, 10);
      if (!Number.isInteger(idx)) return null;
      if (idx < 0) return `d${idx}`; // 设计阶段等负数编号单独成组，仍走旧命名
      return LEGACY_TO_ENGINE_PHASES[idx] ?? 8; // 旧编号越界按已竣工兜底
    };
    for (const f of list) {
      const ei = engineIdxOf(f);
      const key = ei === null ? "unknown" : String(ei);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    const keys = [...map.keys()].filter((k) => k !== "unknown").sort((a, b) => Number(a) - Number(b));
    if (map.has("unknown")) keys.push("unknown");
    const labelOf = (k) => (String(k).startsWith("d") ? getPhase(parseInt(k.slice(1), 10)) : ENGINE_PHASES[Number(k)] || null);
    return keys.map((k) => ({ key: k, phase: k === "unknown" ? null : labelOf(k), items: map.get(k) }));
  }, [visibleFiles, photosOnly]);

  /* 上传核心：点「上传」按钮（系统文件框）与拖拽落到类别区，最终都走这里，保证两条入口行为一致。
     需求 SYS-003：拖拽要支持多选，上传后仍按 category 归档（查看「按阶段」时按项目当前阶段自动归组）。 */
  const uploadFiles = async (cat, files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const lossless = LOSSLESS_CATS.has(cat);
    for (const file of list) {
      try {
        setUploading({ cat, name: file.name, pct: 0 });
        // 批准图纸关闭前端缩图与服务端媒体压缩，确保归档和下载都是原始清晰度；
        // 工地照片归档会生成水印副本，并通过既有后台原图补传保留源照片。
        const data = await uploadChunked(api, file, {
          shrink: lossless ? false : undefined,
          skipCompress: lossless,
          keepOriginal: cat === "job_site_photos",
          onPct: (pct) => setUploading({ cat, name: file.name, pct }),
        });
        const { data: added } = await api.post(`/projects/${projectId}/files`, { category: cat, fileId: data.fileId, filename: data.filename, contentType: data.contentType, kind: data.kind });
        // 「签约归档」会做门牌软校验：只提示不拦截（见 backend/contract_guard.py）
        showContractWarnings(added?.warnings, zh);
        await load();
      } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
      finally { setUploading(null); }
    }
  };
  const onPick = async (cat, e) => {
    const list = Array.from(e.target.files || []); e.target.value = "";
    await uploadFiles(cat, list);
  };

  /* ---- 拖拽上传（SYS-003）----
     使用 dragenter/dragover/dragleave/drop 配合 depth 计数：拖拽进入子元素也会触发 dragleave，
     只靠布尔值会导致高亮闪烁，所以按层计数，归零才取消高亮。
     只接受「文件」拖拽（dataTransfer.types 含 Files），页面内拖动文字/图片不会误触发。 */
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (cat) => (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current[cat] = (dragDepth.current[cat] || 0) + 1;
    setDragCat(cat);
  };
  const onDragOver = (cat) => (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = uploading ? "none" : "copy";
  };
  const onDragLeave = (cat) => (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current[cat] = Math.max(0, (dragDepth.current[cat] || 0) - 1);
    if (dragDepth.current[cat] === 0) setDragCat((c) => (c === cat ? null : c));
  };
  const onDrop = (cat) => async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current[cat] = 0;
    setDragCat(null);
    if (uploading) { toast.info(zh ? "正在上传，请等当前文件完成后再拖入" : "Upload in progress — please wait"); return; }
    await uploadFiles(cat, e.dataTransfer.files);
  };
  const del = async (f) => { if (!window.confirm(zh ? "删除该文件？" : "Delete?")) return; try { await api.delete(`/projects/${projectId}/files/${f.id}`); load(); } catch (e) { toast.error(e.message); } };
  const [genScope, setGenScope] = useState(false);
  const generateScope = async () => {
    setGenScope(true);
    try { await api.post(`/projects/${projectId}/scope-of-works`); toast.success(zh ? "已生成工作内容文档" : "Scope of Works generated"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败" : "Failed")); }
    setGenScope(false);
  };

  // 所有分组共用文件集合组件，避免列表模式遗漏子文件夹或阶段中的预览回调。
  const collection = (items) => <FileCollection items={items} zh={zh} viewMode={viewMode} openPreview={openPreview} thumbErr={thumbErr} setThumbErr={setThumbErr} setSendFile={setSendFile} del={del} />;

  return (
    /* 兜底：拖到类别区以外（如页面空白）时也阻止浏览器直接打开文件，
       避免用户拖偏后当前页面被一个 PDF/图片顶掉。真正的上传只由类别投放区处理。 */
    <div ref={rootRef} className="border-t border-slate-100 pt-4 mt-4" data-testid="project-files-section"
      onDragOver={(e) => { if (hasFiles(e)) e.preventDefault(); }}
      onDrop={(e) => { if (hasFiles(e)) { e.preventDefault(); setDragCat(null); } }}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><FolderOpen className="h-4 w-4 text-red-600" />Files</p>
        {/* 需求 SYS-003：把「可直接拖拽」写进界面，用户不用先点上传才发现 */}
        <span className="text-[11px] text-slate-400 ml-1" data-testid="files-drag-hint">{zh ? "· 可直接把文件拖到下方对应类别上传（支持多选）" : "· Drop files onto a category below to upload (multiple)"}</span>
        <div className="ml-auto inline-flex rounded-lg border border-slate-200 p-0.5" data-testid="files-view-toggle" role="group" aria-label={zh ? "文件分组" : "Group files"}>
          <Button type="button" variant="ghost" size="sm" aria-pressed={groupBy === "category"} data-testid="files-view-category" onClick={() => setGroupBy("category")} className={`h-8 px-2.5 text-[11px] ${groupBy === "category" ? "bg-brand-500 text-white hover:bg-brand-600 hover:text-white" : "text-slate-500"}`}>{zh ? "按类别" : "By category"}</Button>
          <Button type="button" variant="ghost" size="sm" aria-pressed={groupBy === "phase"} data-testid="files-view-phase" onClick={() => setGroupBy("phase")} className={`h-8 px-2.5 text-[11px] ${groupBy === "phase" ? "bg-brand-500 text-white hover:bg-brand-600 hover:text-white" : "text-slate-500"}`}>{zh ? "按阶段" : "By phase"}</Button>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5" role="group" aria-label={zh ? "文件显示方式" : "File view"}>
          <Button type="button" variant="ghost" size="sm" data-testid="files-display-grid" aria-pressed={viewMode === "grid"} onClick={() => setViewMode("grid")} className={`h-8 gap-1.5 px-2 text-[11px] ${viewMode === "grid" ? "bg-brand-50 text-brand-600" : "text-slate-500"}`}><LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />{zh ? "图标" : "Icons"}</Button>
          <Button type="button" variant="ghost" size="sm" data-testid="files-display-list" aria-pressed={viewMode === "list"} onClick={() => setViewMode("list")} className={`h-8 gap-1.5 px-2 text-[11px] ${viewMode === "list" ? "bg-brand-50 text-brand-600" : "text-slate-500"}`}><List className="h-3.5 w-3.5" aria-hidden="true" />{zh ? "详细列表" : "Details"}</Button>
        </div>
        {groupBy === "phase" && (
          <label className="inline-flex items-center gap-1 text-[11px] text-slate-500 cursor-pointer">
            <input type="checkbox" checked={photosOnly} onChange={(e) => setPhotosOnly(e.target.checked)} data-testid="files-photos-only" />{zh ? "只看照片/视频" : "Photos & videos"}
          </label>
        )}
      </div>
      <div className="space-y-3">
        {groupBy === "category" && CATS.filter((c) => (canSeeRestricted || !ADMIN_ONLY_CATS.includes(c.key)) && (!categoryFilter || c.key === categoryFilter)).map((c) => {
          // 「工地现场照片」按 job_site_photos + engine_evidence 的图片/视频并集展示（口径见 SITE_MEDIA_CATS）；
          // 证据类记录标 immutable：本页不提供删除，删除仍走施工任务证据本身。
          const items = visibleFiles.filter((f) => matchesCat(f, c.key))
            .map((f) => (f.category === c.key ? f : { ...f, immutable: true, kind: mediaKind(f) }));
          // 需求反馈 FBfdef4adef6（2026-10-14，CEO）：不再显示空的「签约归档 Signed Archive」占位行。
          // 该分类是合同 / 报价单 / 分包合同 / 变更单 / Lien Waiver 签署版的**自动归档落点**
          // （backend/sub_rfq.py、sub_contract_ops.py、sub_change_orders.py、lien_waiver.py
          // 签署完成后都会往这里归档），所以不能整行永久删除 —— 那样这些 PDF 在类别视图里会失去入口
          // （反馈当天 CEO 追问了「删除后原本的这些应该如何处理怎么显示」）。
          // 因此只在计数为 0 时隐藏整行：CEO 平时看到的空占位消失，一旦真的归档了文件照常展示。
          // 注意 2026-10-16 起 signed_archive 已并入 ADMIN_ONLY_CATS（仅 admin / CEO 可见），
          // 普通角色本来就看不到这一行，本条只影响 admin / CEO 视角。
          // 按阶段视图（groupBy === "phase"）不按分类过滤，不受影响。
          if (c.key === "signed_archive" && items.length === 0) return null;
          const dragging = dragCat === c.key;
          const flash = flashCat === c.key;
          return (
            <div key={c.key} data-testid={`file-cat-${c.key}`}
              onDragEnter={onDragEnter(c.key)} onDragOver={onDragOver(c.key)} onDragLeave={onDragLeave(c.key)} onDrop={onDrop(c.key)}
              className={`rounded-lg transition-colors ${dragging ? "bg-brand-50/70 ring-2 ring-brand-300 ring-offset-1" : ""} ${flash ? "bg-brand-50/60 ring-2 ring-brand-200 ring-offset-1" : ""}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="text-xs font-medium text-slate-600">{zh ? c.zh : c.zh.split(" ").slice(1).join(" ")}</span>
                <span className="text-[10px] text-slate-400">· {items.length}</span>
                <input ref={(el) => (refs.current[c.key] = el)} type="file" multiple accept="image/*,video/*,application/pdf" className="hidden" onChange={(e) => onPick(c.key, e)} data-testid={`file-input-${c.key}`} />
                {c.key === "scope_of_works" && (
                  <Button data-testid="project-gen-scope" variant="outline" size="sm" className="ml-auto text-[11px] h-6 px-2 border-red-200 text-red-700" onClick={generateScope} disabled={genScope}>{genScope ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSignature className="h-3 w-3 mr-1" />}{zh ? "生成工作内容" : "Generate"}</Button>
                )}
                <Button data-testid={`file-upload-${c.key}`} variant="outline" size="sm" className={`${c.key === "scope_of_works" ? "" : "ml-auto"} text-[11px] h-6 px-2`} onClick={() => refs.current[c.key]?.click()}><Upload className="h-3 w-3 mr-1" />{zh ? "上传" : "Upload"}</Button>
              </div>
              {uploading && uploading.cat === c.key && (
                <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1.5"><Loader2 className="h-3 w-3 animate-spin" /><span className="truncate max-w-[120px]">{uploading.name}</span><div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-red-500" style={{ width: `${uploading.pct}%` }} /></div>{uploading.pct}%</div>
              )}
              <div>
                {collection(items.filter((f) => !f.folder))}
                {items.length === 0 && !dragging && <p className="col-span-full text-[11px] text-slate-300 py-1">—</p>}
                {/* 拖拽悬停提示：让「可以拖到这里」这件事在投放前就可见（SYS-003） */}
                {dragging && (
                  <div className="col-span-full flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-brand-400 bg-white/70 py-4 text-brand-600" data-testid={`file-drop-hint-${c.key}`}>
                    <Upload className="h-5 w-5" />
                    <p className="text-[11px] font-semibold">{zh ? "松开即上传到此类别（支持多选）" : "Drop to upload here (multiple)"}</p>
                  </div>
                )}
              </div>
              {[...new Set(items.filter((f) => f.folder).map((f) => f.folder))].map((folder) => (
                <div key={folder} className="mt-2 rounded-md border border-dashed border-slate-200 bg-slate-50/60 p-2" data-testid={`file-folder-${c.key}-${folder}`}>
                  <p className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><FolderOpen className="h-3 w-3 text-amber-500" />{folder}<span className="text-[10px] text-slate-400 font-normal">· {items.filter((f) => f.folder === folder).length}</span></p>
                  {collection(items.filter((f) => f.folder === folder))}
                </div>
              ))}
            </div>
          );
        })}
        {groupBy === "phase" && (
          <>
            {phaseGroups.length === 0 && <p className="text-[11px] text-slate-300 py-1">{zh ? "暂无文件" : "No files"}</p>}
            {phaseGroups.map((g) => (
              <div key={g.key} data-testid={`file-phase-${g.key}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: g.phase?.color || "#94a3b8" }} />
                  <span className="text-xs font-medium text-slate-600">{g.phase ? (zh ? g.phase.zh : g.phase.en) : (zh ? "未标注阶段（旧文件）" : "Unlabeled (legacy)")}</span>
                  <span className="text-[10px] text-slate-400">· {g.items.length}</span>
                </div>
                {collection(g.items)}
              </div>
            ))}
          </>
        )}
      </div>

      <SendDrawingDialog open={!!sendFile} onClose={() => setSendFile(null)} api={api} zh={zh} projectId={projectId} file={sendFile} />
      <PlanShareDialog open={!!shareFile} onClose={() => setShareFile(null)} api={api} zh={zh} projectId={projectId} file={shareFile} onRfq={(f) => setSendFile(f)} />

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="z-[95] max-w-[96vw] w-[96vw] h-[90dvh] max-h-[90dvh] p-2 sm:p-3 gap-2 grid-rows-[auto_minmax(0,1fr)] overflow-hidden" overlayClassName="z-[94]" data-testid="file-preview-modal"
          closeLabel={zh ? "关闭预览" : "Close preview"} aria-describedby={undefined} onKeyDown={onPreviewKey}
          onCloseAutoFocus={(event) => { if (previewTrigger.current?.isConnected) { event.preventDefault(); previewTrigger.current.focus(); } }}>
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-slate-800 truncate pr-8 flex items-center gap-2">
              <FileText className="h-4 w-4 text-brand-500 shrink-0" /><span className="truncate">{preview?.filename}</span>
              {preview?.category && <span className="hidden sm:inline text-[10px] font-medium text-slate-400 shrink-0">· {CATS.find((c) => c.key === preview.category)?.zh.split(" ").slice(zh ? 0 : 1).join(" ")}</span>}
            </DialogTitle>
          </DialogHeader>
          {preview && preview.kind === "pdf" && (
            <div className="min-h-0 h-full">
              <ProjectPdfPreview api={api} file={preview} zh={zh} extra={preview.category === "approved_plan" && (
                <button type="button" onClick={() => setShareFile(preview)} data-testid="file-preview-share-sub" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold shadow-sm transition-colors"><Send className="h-3.5 w-3.5" />{zh ? "发给分包" : "Send to sub"}</button>
              )} />
            </div>
          )}
          {preview && preview.kind !== "pdf" && (
            <div className="flex min-h-0 flex-col gap-2">
              {/* 照片占用弹窗剩余高度，文件名、缩放与翻页控件始终可见；长图不把底部操作挤出屏幕。 */}
              <div className="relative min-h-0 flex-1">
                {previewQueue.length > 1 && (
                  <>
                    <button type="button" onClick={(e) => { e.stopPropagation(); stepPreview(-1); }} aria-label={zh ? "上一张" : "Previous"} data-testid="file-preview-prev"
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-slate-900/70 hover:bg-slate-800 text-white flex items-center justify-center shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"><ChevronLeft className="h-6 w-6" aria-hidden="true" /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); stepPreview(1); }} aria-label={zh ? "下一张" : "Next"} data-testid="file-preview-next"
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-slate-900/70 hover:bg-slate-800 text-white flex items-center justify-center shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"><ChevronRight className="h-6 w-6" aria-hidden="true" /></button>
                  </>
                )}
                {preview.kind === "image"
                  ? <ProjectFileImagePreview key={preview.id} url={fileUrl(preview.fileId)} name={preview.filename} zh={zh} onStep={stepPreview} />
                  : preview.kind === "video"
                    ? <video key={preview.id} src={fileUrl(preview.fileId)} controls playsInline className="h-full w-full rounded-md border border-slate-200 bg-black object-contain" />
                    : <iframe title={preview.filename} src={fileUrl(preview.fileId)} className="h-full w-full rounded-md border border-slate-200 bg-white" data-testid="file-preview-frame" />}
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                {/* 队列计数：让用户知道「这一批还有多少、现在第几张」，避免翻到底还以为没加载出来 */}
                <span className="text-xs text-slate-500" data-testid="file-preview-counter" role="status" aria-live="polite" aria-atomic="true">
                  {previewQueue.length > 1 && queuePos >= 0
                    ? (zh ? `第 ${queuePos + 1} / ${previewQueue.length} 项` : `${queuePos + 1} / ${previewQueue.length}`)
                    : (zh ? "共 1 项" : "1 item")}
                </span>
                <div className="flex flex-wrap items-center gap-3">
                {/* 原图链接指向上传文件，src=1 会在后台补传完成后取到原始字节，水印图保持不变。 */}
                {preview.watermark && preview.originalFileId && <a data-testid="file-preview-original" href={`${fileUrl(preview.originalFileId)}&src=1`} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center text-xs text-slate-500 hover:text-brand-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">{zh ? "查看原图" : "View original"}</a>}
                <a data-testid="file-preview-newtab" href={fileUrl(preview.fileId)} target="_blank" rel="noreferrer" className="inline-flex min-h-9 items-center gap-1.5 text-xs text-slate-500 hover:text-brand-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                  <ExternalLink className="h-3.5 w-3.5" />{zh ? "在新标签页打开 / 下载" : "Open in new tab / download"}
                </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
